/**
 * Vincent B946 (Eric 2026-02-14 SPEC STRICK PROD) — Historique des corrections
 * factuelles (audit Loi 25).
 *
 * Liste toutes les corrections factuelles appliquées à travers l'org via B943
 * (verify + apply-corrections). Chaque ligne : vidéo, source, extrait erroné,
 * correction, source de validation, date d'application. Filtre par vidéo,
 * source_type, texte libre. Export CSV pour l'audit Loi 25.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import BulkVerifyModal from "../../components/admin/BulkVerifyModal";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Tâche 1382 — Table hors composant : elle ne peut pas appeler le traducteur
// (leçon 1373 — un appel hors du hook rend l'écran blanc). Les
// deux langues vivent sur la même entrée, motif 1324b. Les clés (`ku:qa`…)
// sont des valeurs du serveur : elles ne se traduisent jamais.
const SOURCE_LABEL = {
  whisper: { fr: "🎙️ Whisper", en: "🎙️ Whisper" },
  fireflies: { fr: "🔥 Fireflies", en: "🔥 Fireflies" },
  merged: { fr: "🔀 Fusionnée", en: "🔀 Merged" },
  "ku:synthesis": { fr: "📄 Synthèse", en: "📄 Synthesis" },
  "ku:qa": { fr: "❓ Q&R", en: "❓ Q&A" },
  "ku:concept": { fr: "💡 Concept", en: "💡 Concept" },
  "ku:procedure": { fr: "📋 Procédure", en: "📋 Procedure" },
  "ku:objection": { fr: "🛡️ Objection", en: "🛡️ Objection" },
};

const sourceLabel = (t, lang) => {
  const e = SOURCE_LABEL[t];
  if (!e) return t || "?";
  return (lang === "en" ? e.en : e.fr) || e.fr;
};

export default function FactualCorrectionsAdminPage() {
  const { tr, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [filterVideo, setFilterVideo] = useState("");
  // Vincent B947 (Eric 2026-02-14) — Modal Bulk Verify.
  const [bulkOpen, setBulkOpen] = useState(false);

  // Vincent B952 (Eric 2026-02-14 SPEC STRICK PROD) — Marque la page comme
  // visitée à l'ouverture, ce qui reset le badge menu au prochain poll.
  useEffect(() => {
    try {
      localStorage.setItem(
        "knoria_last_visited_factual_corrections",
        new Date().toISOString(),
      );
    } catch { /* silencieux */ }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/factual-corrections/history?limit=1000`,
        { withCredentials: true },
      );
      setRows(data?.corrections || []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterType && r.source_type !== filterType) return false;
      if (filterVideo && r.video_id !== filterVideo) return false;
      if (filterQuery) {
        const q = filterQuery.toLowerCase();
        const hay = `${r.original} ${r.corrected} ${r.source} ${r.issue}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filterType, filterVideo, filterQuery]);

  const uniqVideos = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.video_id) map.set(r.video_id, r.video_title || r.video_id); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const uniqTypes = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.source_type) s.add(r.source_type); });
    return Array.from(s).sort();
  }, [rows]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error(tr(
        "Aucune ligne à exporter (ajuste tes filtres).",
        "No row to export (adjust your filters).",
      ));
      return;
    }
    const escape = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ["applied_at", "video_id", "video_title", "source_type", "original", "corrected", "source", "issue"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push(header.map((h) => escape(r[h])).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factual-corrections-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(lang === "fr"
      ? `${filtered.length} ligne(s) exportée(s).`
      : `${filtered.length} row(s) exported.`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="factual-corrections-admin-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">
            📋 {tr("Historique des corrections factuelles", "Factual corrections history")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            {tr(
              "Toutes les corrections appliquées via « Vérifier sur le web ». Utilisé pour l'audit Loi 25 : chaque ligne inclut la source de validation.",
              "Every correction applied through \"Check on the web\". Used for the Law 25 audit: each row carries its validation source.",
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-blue-700 text-white hover:bg-blue-800"
            data-testid="factual-corrections-bulk-verify-btn"
          >
            🚀 {tr("Vérifier en masse", "Verify in bulk")}
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="factual-corrections-refresh-btn"
          >
            {loading ? tr("⏳ Chargement…", "⏳ Loading…") : tr("↻ Recharger", "↻ Reload")}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            data-testid="factual-corrections-export-csv-btn"
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded p-3 mb-3 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder={tr("Rechercher dans erreur / correction / source / motif…", "Search in error / correction / source / reason…")}
          className="flex-1 min-w-[200px] text-sm px-2 py-1.5 border border-zinc-300 rounded focus:ring-2 focus:ring-teal-500"
          data-testid="factual-corrections-search"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-sm px-2 py-1.5 border border-zinc-300 rounded bg-white"
          data-testid="factual-corrections-filter-type"
        >
          <option value="">{tr("Toutes sources", "All sources")}</option>
          {uniqTypes.map((t) => (
            <option key={t} value={t}>{sourceLabel(t, lang)}</option>
          ))}
        </select>
        <select
          value={filterVideo}
          onChange={(e) => setFilterVideo(e.target.value)}
          className="text-sm px-2 py-1.5 border border-zinc-300 rounded bg-white max-w-[240px]"
          data-testid="factual-corrections-filter-video"
        >
          <option value="">{tr("Toutes vidéos", "All videos")}</option>
          {uniqVideos.map(([id, title]) => (
            <option key={id} value={id}>{title.slice(0, 40)}</option>
          ))}
        </select>
        <span className="text-xs text-zinc-500 ml-2">
          {filtered.length} / {total} {tr("correction(s)", "correction(s)")}
        </span>
      </div>

      {filtered.length === 0 && !loading ? (
        <p
          className="text-sm text-zinc-500 italic bg-white border border-zinc-200 rounded p-6 text-center"
          data-testid="factual-corrections-empty"
        >
          {tr("Aucune correction factuelle", "No factual correction")}
          {(filterType || filterVideo || filterQuery)
            ? tr(" (avec ces filtres)", " (with these filters)")
            : ""}.{" "}
          {tr(
            "Utilise « Vérifier sur le web » depuis l'onglet Synthèse d'une vidéo pour en créer.",
            "Use \"Check on the web\" from a video's Synthesis tab to create some.",
          )}
        </p>
      ) : (
        <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="factual-corrections-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Date", "Date")}</th>
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Vidéo", "Video")}</th>
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Source", "Source")}</th>
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Erreur", "Error")}</th>
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Correction", "Correction")}</th>
                <th className="text-left px-2 py-2 font-semibold text-zinc-700">{tr("Source de validation", "Validation source")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr
                  key={i}
                  className="border-b border-zinc-100 hover:bg-zinc-50/50"
                  data-testid={`factual-correction-row-${i}`}
                >
                  <td className="px-2 py-2 whitespace-nowrap text-zinc-600">
                    {r.applied_at
                      ? new Date(r.applied_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")
                      : "—"}
                  </td>
                  <td className="px-2 py-2 text-zinc-700 max-w-[200px] truncate" title={r.video_title}>
                    <a
                      href={`/dashboard/videos/${r.video_id}?tab=synthesis`}
                      className="text-blue-700 hover:underline"
                      data-testid={`factual-correction-video-link-${i}`}
                    >
                      {r.video_title}
                    </a>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">{sourceLabel(r.source_type, lang)}</td>
                  <td className="px-2 py-2 text-red-800 max-w-[240px]">
                    <div className="line-through opacity-80">{r.original}</div>
                    {r.issue && <div className="text-[10px] text-amber-700 mt-0.5">{r.issue}</div>}
                  </td>
                  <td className="px-2 py-2 text-emerald-800 font-medium max-w-[240px]">
                    {r.corrected}
                  </td>
                  <td className="px-2 py-2 max-w-[240px] break-all">
                    {r.source && /^https?:\/\//.test(r.source) ? (
                      <a
                        href={r.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline"
                        data-testid={`factual-correction-source-link-${i}`}
                      >
                        {r.source}
                      </a>
                    ) : (
                      <span className="text-zinc-600">{r.source || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <BulkVerifyModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={load}
      />
    </div>
  );
}
